// src/pages/cart/CartPage.tsx
// Web adaptation of mobile CartScreen - Shopping cart functionality

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Plus, Minus, Trash2, ArrowRight, Package,
  Loader2, CreditCard, MapPin, Clock, AlertCircle, CheckCircle,
  Tag, Gift
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';
import { getImageUrl } from '@/config/media';

interface CartItem {
  id: string;
  product_id?: string;
  service_id?: string;
  name: string;
  price: number;
  quantity: number;
  image_url?: string;
  type: 'product' | 'service';
  business_id: string;
  business_name: string;
  variations?: any;
  notes?: string;
}

interface Promotion {
  id: string;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_purchase?: number;
  is_active: boolean;
}

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<Promotion | null>(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get('/cart');
      setCartItems(data.items || []);
    } catch (error: any) {
      console.error('Error fetching cart:', error);
      toastError('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;

    setUpdating(itemId);
    try {
      await apiClient.patch(`/cart/${itemId}`, { quantity: newQuantity });
      fetchCart();
    } catch (error: any) {
      toastError('Failed to update quantity');
    } finally {
      setUpdating(null);
    }
  };

  const removeItem = async (itemId: string) => {
    try {
      await apiClient.delete(`/cart/${itemId}`);
      toastSuccess('Item removed from cart');
      fetchCart();
    } catch (error: any) {
      toastError('Failed to remove item');
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      toastError('Please enter a promo code');
      return;
    }

    try {
      const { data } = await apiClient.post('/promotions/validate', { code: promoCode.trim() });
      setAppliedPromo(data);
      toastSuccess('Promo code applied!');
    } catch (error: any) {
      toastError(error.response?.data?.error || 'Invalid promo code');
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateDiscount = () => {
    if (!appliedPromo) return 0;
    const subtotal = calculateSubtotal();
    if (appliedPromo.min_purchase && subtotal < appliedPromo.min_purchase) return 0;

    if (appliedPromo.discount_type === 'percentage') {
      return (subtotal * appliedPromo.discount_value) / 100;
    }
    return appliedPromo.discount_value;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + deliveryFee - calculateDiscount();
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toastError('Your cart is empty');
      return;
    }

    setCheckoutLoading(true);
    try {
      const orderData = {
        items: cartItems.map(item => ({
          id: item.id,
          quantity: item.quantity,
          type: item.type,
        })),
        delivery_address: deliveryAddress,
        delivery_fee: deliveryFee,
        promo_code: appliedPromo?.code,
        total: calculateTotal(),
      };

      const { data } = await apiClient.post('/orders', orderData);
      toastSuccess('Order placed successfully!');
      navigate(`/orders/${data.order_id}`);
    } catch (error: any) {
      toastError('Failed to place order');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3">
          <h1 className="font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Shopping Cart
          </h1>
        </div>
        <div className="flex items-center justify-center py-24">
          <Loader2 size={40} className="animate-spin mx-auto text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-32">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3">
        <h1 className="font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
          Shopping Cart
        </h1>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Cart Items */}
        {cartItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
            <ShoppingCart size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 18 }}>
              Your Cart is Empty
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              Start shopping and add items to your cart
            </p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 px-6 py-3 rounded-xl text-white font-semibold"
              style={{ backgroundColor: '#4F46E5' }}
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {cartItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-4 bg-light-card dark:bg-dark-card rounded-xl p-4 shadow-sm border border-light-border dark:border-dark-border"
              >
                {/* Image */}
                <div className="w-20 h-20 rounded-lg bg-light-bg dark:bg-dark-bg flex-shrink-0 overflow-hidden">
                  {item.image_url ? (
                    <img
                      src={getImageUrl(item.image_url)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package size={24} className="text-light-subtle dark:text-dark-subtle" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-light-text dark:text-dark-text truncate">
                    {item.name}
                  </p>
                  <p className="text-xs text-light-subtle dark:text-dark-subtle">
                    {item.business_name}
                  </p>
                  <p className="font-bold text-primary mt-1">
                    KES {item.price.toLocaleString()}
                  </p>
                </div>

                {/* Quantity Controls */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    disabled={updating === item.id}
                    className="w-8 h-8 rounded-lg bg-light-bg dark:bg-dark-bg flex items-center justify-center hover:bg-light-border dark:hover:bg-dark-border transition-colors disabled:opacity-50"
                  >
                    <Minus size={14} />
                  </button>
                  <span className="w-8 text-center font-semibold text-light-text dark:text-dark-text">
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    disabled={updating === item.id}
                    className="w-8 h-8 rounded-lg bg-light-bg dark:bg-dark-bg flex items-center justify-center hover:bg-light-border dark:hover:bg-dark-border transition-colors disabled:opacity-50"
                  >
                    <Plus size={14} />
                  </button>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => removeItem(item.id)}
                  className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Promo Code */}
        {cartItems.length > 0 && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 shadow-sm border border-light-border dark:border-dark-border">
            <h2 className="font-semibold text-sm mb-3 text-light-text dark:text-dark-text">
              Have a Promo Code?
            </h2>
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
                <input
                  type="text"
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <button
                onClick={applyPromoCode}
                disabled={!promoCode.trim()}
                className="px-4 py-3 rounded-xl text-white font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: '#4F46E5' }}
              >
                Apply
              </button>
            </div>
            {appliedPromo && (
              <div className="mt-3 flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <CheckCircle size={16} className="text-green-500" />
                <span className="text-sm text-green-600 dark:text-green-400">
                  {appliedPromo.discount_type === 'percentage'
                    ? `${appliedPromo.discount_value}% off applied`
                    : `KES ${appliedPromo.discount_value} off applied`}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Delivery Address */}
        {cartItems.length > 0 && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 shadow-sm border border-light-border dark:border-dark-border">
            <h2 className="font-semibold text-sm mb-3 text-light-text dark:text-dark-text flex items-center gap-2">
              <MapPin size={16} />
              Delivery Address
            </h2>
            <textarea
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              placeholder="Enter your delivery address"
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
            />
          </div>
        )}

        {/* Order Summary */}
        {cartItems.length > 0 && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 shadow-sm border border-light-border dark:border-dark-border">
            <h2 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">
              Order Summary
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-light-subtle dark:text-dark-subtle">Subtotal</span>
                <span className="font-medium text-light-text dark:text-dark-text">
                  KES {calculateSubtotal().toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-light-subtle dark:text-dark-subtle">Delivery Fee</span>
                <span className="font-medium text-light-text dark:text-dark-text">
                  KES {deliveryFee.toLocaleString()}
                </span>
              </div>
              {appliedPromo && calculateDiscount() > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-green-500">Discount</span>
                  <span className="font-medium text-green-500">
                    -KES {calculateDiscount().toLocaleString()}
                  </span>
                </div>
              )}
              <div className="border-t border-light-border dark:border-dark-border pt-3 flex justify-between">
                <span className="font-semibold text-light-text dark:text-dark-text">Total</span>
                <span className="font-bold text-lg" style={{ color: '#4F46E5' }}>
                  KES {calculateTotal().toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Checkout Bar */}
      {cartItems.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-light-card dark:bg-dark-card border-t border-light-border dark:border-dark-border px-4 py-4 safe-area-pb">
          <div className="max-w-3xl mx-auto flex items-center gap-4">
            <div className="flex-1">
              <p className="text-xs text-light-subtle dark:text-dark-subtle">Total ({cartItems.length} items)</p>
              <p className="font-bold text-lg" style={{ color: '#4F46E5' }}>
                KES {calculateTotal().toLocaleString()}
              </p>
            </div>
            <button
              onClick={handleCheckout}
              disabled={checkoutLoading}
              className="flex items-center gap-2 px-6 py-4 rounded-xl text-white font-semibold transition-all hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#4F46E5' }}
            >
              {checkoutLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard size={18} />
                  Checkout
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;